<template>
  <v-container fluid class="pa-6 pa-md-8">
    <div class="page-head d-flex flex-wrap align-center justify-space-between">
      <div>
        <h1 class="page-head__title">MSISDN Statistics</h1>
        <p class="page-head__sub">
          Transaction performance grouped by phone number.
        </p>
      </div>
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="dates"
        transition="scale-transition"
        offset-y
        min-width="auto"
        :nudge-left="120"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            outlined
            v-bind="attrs"
            v-on="on"
            class="toolbar-btn text-none"
            :class="{ 'toolbar-btn--active': dates.length > 0 }"
          >
            <v-icon small left>mdi-calendar-range-outline</v-icon>
            {{ dates.length > 0 ? dateRangeText : "Choose date range" }}
            <v-icon
              v-if="dates.length === 2"
              small
              right
              @click.stop="clearDateFilter"
              >mdi-close</v-icon
            >
          </v-btn>
        </template>
        <v-date-picker v-model="dates" range :max="today" color="primary">
          <v-spacer></v-spacer>
          <v-btn small text color="primary" @click="menu = false">Cancel</v-btn>
          <v-btn @click="setFilteredDate" text color="primary">OK</v-btn>
        </v-date-picker>
      </v-menu>
    </div>

    <!-- Summary tiles -->
    <v-row class="mb-1">
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-tile">
          <div class="stat-tile__icon stat-tile__icon--brand">
            <v-icon size="20">mdi-phone-in-talk</v-icon>
          </div>
          <div>
            <p class="stat-tile__label">Total MSISDNs</p>
            <p class="stat-tile__value tabular">
              {{ totalMsisdns | compactNumber }}
            </p>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-tile">
          <div class="stat-tile__icon stat-tile__icon--success">
            <v-icon size="20">mdi-check-circle-outline</v-icon>
          </div>
          <div>
            <p class="stat-tile__label">Successful</p>
            <p class="stat-tile__value tabular">
              {{ totalSuccessful | compactNumber }}
            </p>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-tile">
          <div class="stat-tile__icon stat-tile__icon--failed">
            <v-icon size="20">mdi-alert-circle-outline</v-icon>
          </div>
          <div>
            <p class="stat-tile__label">Failed</p>
            <p class="stat-tile__value tabular">
              {{ totalFailed | compactNumber }}
            </p>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-tile">
          <div class="stat-tile__icon stat-tile__icon--info">
            <v-icon size="20">mdi-cash-multiple</v-icon>
          </div>
          <div>
            <p class="stat-tile__label">Total Amount</p>
            <p class="stat-tile__value tabular">
              {{ totalAmount | compactCurrency }}
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Top 5 chart -->
    <div class="panel mb-6">
      <div class="panel__head">
        <v-icon small class="mr-2">mdi-star-outline</v-icon>
        Top 5 MSISDNs by Transaction Amount
      </div>
      <div class="panel__body">
        <apexchart
          v-if="!pageLoading && topMsisdns.length > 0"
          type="bar"
          height="350"
          :options="topMsisdnsChartOptions"
          :series="topMsisdnsSeries"
        ></apexchart>
        <div v-else-if="pageLoading" class="panel__empty">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else class="panel__empty text-muted">No data available</div>
      </div>
    </div>

    <v-row>
      <v-col cols="12" lg="6">
        <div class="panel">
          <div class="panel__head">
            <v-icon small class="mr-2">mdi-chart-bar</v-icon>
            Success vs Failed by MSISDN
          </div>
          <div class="panel__body">
            <apexchart
              v-if="!pageLoading && successFailedData.length > 0"
              type="bar"
              height="350"
              :options="successFailedChartOptions"
              :series="successFailedSeries"
            ></apexchart>
            <div v-else-if="pageLoading" class="panel__empty">
              <v-progress-circular indeterminate color="success" />
            </div>
            <div v-else class="panel__empty text-muted">No data available</div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" lg="6">
        <div class="panel">
          <div class="panel__head">
            <v-icon small class="mr-2">mdi-trending-up</v-icon>
            Success Rate by MSISDN
          </div>
          <div class="panel__body">
            <apexchart
              v-if="!pageLoading && successFailedData.length > 0"
              type="line"
              height="350"
              :options="successRateChartOptions"
              :series="successRateSeries"
            ></apexchart>
            <div v-else-if="pageLoading" class="panel__empty">
              <v-progress-circular indeterminate color="info" />
            </div>
            <div v-else class="panel__empty text-muted">No data available</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- MSISDN details table -->
    <div class="mt-4">
      <div class="d-flex align-center mb-3">
        <h2 class="section-title mb-0">MSISDN transaction details</h2>
        <v-spacer />
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <v-btn
              :loading="pageLoading"
              @click="refreshData"
              v-on="on"
              icon
              class="toolbar__refresh"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh statistics</span>
        </v-tooltip>
      </div>
      <v-data-table
        :headers="msisdnHeaders"
        :items="msisdnStatistics"
        :loading="pageLoading"
        loading-text="Loading statistics… Please wait"
        class="data-table"
        :footer-props="{ itemsPerPageOptions: [10, 20, 30, 50] }"
      >
        <template #item.msisdn="{ item }">
          <span class="tabular cell-strong">{{ item.msisdn }}</span>
        </template>
        <template #item.totalTransactions="{ item }">
          <span class="tabular">{{ item.totalTransactions }}</span>
        </template>
        <template #item.totalAmount="{ item }">
          <span class="tabular cell-amount">{{
            item.totalAmount | currencyFormat
          }}</span>
        </template>
        <template #item.successfulTransactions="{ item }">
          <span class="pill pill--success">{{
            item.successfulTransactions
          }}</span>
        </template>
        <template #item.failedTransactions="{ item }">
          <span class="pill pill--failed">{{ item.failedTransactions }}</span>
        </template>
        <template #item.pendingTransactions="{ item }">
          <span class="pill pill--pending">{{
            item.pendingTransactions || 0
          }}</span>
        </template>
        <template #item.successRate="{ item }">
          <v-progress-linear
            :value="item.successRate"
            :color="getSuccessRateColor(item.successRate)"
            height="20"
            rounded
          >
            <template v-slot:default>
              <small class="white--text font-weight-bold"
                >{{ item.successRate.toFixed(1) }}%</small
              >
            </template>
          </v-progress-linear>
        </template>
      </v-data-table>
    </div>

    <!-- Success/Failed breakdown table -->
    <div class="mt-6">
      <h2 class="section-title">Success / failed breakdown</h2>
      <v-data-table
        :headers="successFailedHeaders"
        :items="successFailedData"
        :loading="pageLoading"
        loading-text="Loading statistics… Please wait"
        class="data-table"
        :options.sync="successFailedOptions"
        :server-items-length="successFailedPagination.totalRecords"
        :footer-props="{ itemsPerPageOptions: [10, 20, 30, 50] }"
      >
        <template #item.msisdn="{ item }">
          <span class="tabular cell-strong">{{ item.msisdn }}</span>
        </template>
        <template #item.successfulTransactions="{ item }">
          <span class="tabular cell-success">{{
            item.successfulTransactions
          }}</span>
        </template>
        <template #item.failedTransactions="{ item }">
          <span class="tabular cell-failed">{{
            item.failedTransactions
          }}</span>
        </template>
        <template #item.pendingTransactions="{ item }">
          <span class="tabular cell-ember">{{
            item.pendingTransactions || 0
          }}</span>
        </template>
        <template #item.successfulAmount="{ item }">
          <span class="tabular cell-success">{{
            item.successfulAmount | currencyFormat
          }}</span>
        </template>
        <template #item.failedAmount="{ item }">
          <span class="tabular cell-failed">{{
            item.failedAmount | currencyFormat
          }}</span>
        </template>
        <template #item.pendingAmount="{ item }">
          <span class="tabular cell-ember">{{
            item.pendingAmount | currencyFormat
          }}</span>
        </template>
        <template #item.totalTransactions="{ item }">
          <span class="tabular">{{ item.totalTransactions }}</span>
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import SnackBarMixins from "@/mixins/SnackBarMIxins";

export default {
  name: "MsisdnStatistics",
  mixins: [SnackBarMixins],
  data() {
    return {
      menu: false,
      dates: [],
      today: moment().format("YYYY-MM-DD"),
      successFailedOptions: {
        page: 1,
        itemsPerPage: 10
      }
    };
  },
  computed: {
    ...mapGetters({
      pageLoading: "getIsPageLoading",
      msisdnStatistics: "reports/getMsisdnStatistics",
      topMsisdns: "reports/getTopMsisdns",
      successFailedData: "reports/getSuccessFailedByMsisdn",
      successFailedPagination: "reports/getSuccessFailedByMsisdnPagination"
    }),
    isDark() {
      return this.$vuetify.theme.dark;
    },
    chartTheme() {
      return {
        foreColor: this.isDark ? "#9aa3ae" : "#545d68",
        grid: this.isDark ? "rgba(255,255,255,0.08)" : "#e4e7eb",
        mode: this.isDark ? "dark" : "light"
      };
    },
    dateRangeText() {
      if (this.dates.length === 2) {
        const sortedDates = [...this.dates].sort();
        const [start, end] = sortedDates;
        return `${moment(start).format("MMM DD")} ~ ${moment(end).format(
          "MMM DD, YYYY"
        )}`;
      }
      return this.dates.join(" ~ ");
    },
    totalMsisdns() {
      return this.msisdnStatistics.length;
    },
    totalSuccessful() {
      return this.msisdnStatistics.reduce(
        (sum, item) => sum + (item.successfulTransactions || 0),
        0
      );
    },
    totalFailed() {
      return this.msisdnStatistics.reduce(
        (sum, item) => sum + (item.failedTransactions || 0),
        0
      );
    },
    totalAmount() {
      return this.msisdnStatistics.reduce(
        (sum, item) => sum + (item.totalAmount || 0),
        0
      );
    },
    msisdnHeaders() {
      return [
        {
          text: "Phone Number",
          value: "msisdn",
          filterable: true,
          sortable: true
        },
        {
          text: "Total Transactions",
          value: "totalTransactions",
          filterable: false,
          sortable: true
        },
        {
          text: "Total Amount (GHS)",
          value: "totalAmount",
          filterable: false,
          sortable: true
        },
        {
          text: "Successful",
          value: "successfulTransactions",
          filterable: false,
          sortable: true
        },
        {
          text: "Failed",
          value: "failedTransactions",
          filterable: false,
          sortable: true
        },
        {
          text: "Pending",
          value: "pendingTransactions",
          filterable: false,
          sortable: true
        },
        {
          text: "Success Rate",
          value: "successRate",
          filterable: false,
          sortable: true
        }
      ];
    },
    successFailedHeaders() {
      return [
        {
          text: "Phone Number",
          value: "msisdn",
          filterable: true,
          sortable: true
        },
        {
          text: "Successful Count",
          value: "successfulTransactions",
          filterable: false,
          sortable: true
        },
        {
          text: "Failed Count",
          value: "failedTransactions",
          filterable: false,
          sortable: true
        },
        {
          text: "Pending Count",
          value: "pendingTransactions",
          filterable: false,
          sortable: true
        },
        {
          text: "Successful Amount",
          value: "successfulAmount",
          filterable: false,
          sortable: true
        },
        {
          text: "Failed Amount",
          value: "failedAmount",
          filterable: false,
          sortable: true
        },
        {
          text: "Pending Amount",
          value: "pendingAmount",
          filterable: false,
          sortable: true
        },
        {
          text: "Total Transactions",
          value: "totalTransactions",
          filterable: false,
          sortable: true
        }
      ];
    },
    topMsisdnsSeries() {
      return [
        {
          name: "Total Amount (GHS)",
          data: this.topMsisdns.map(item => item.totalAmount || 0)
        }
      ];
    },
    topMsisdnsChartOptions() {
      return {
        chart: {
          type: "bar",
          height: 350,
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: { show: true }
        },
        theme: { mode: this.chartTheme.mode },
        colors: ["#ff0055"],
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
            distributed: false,
            dataLabels: { position: "top" }
          }
        },
        dataLabels: {
          enabled: true,
            formatter: val => this.$options.filters.compactCurrency(val),
          offsetX: 30,
          style: {
            fontSize: "12px",
            colors: [this.chartTheme.foreColor]
          }
        },
        xaxis: {
          categories: this.topMsisdns.map(item => item.msisdn || ""),
          title: { text: "Total Amount (GHS)" },
          labels: {
            formatter: value => this.$options.filters.compactCurrency(value)
          }
        },
        yaxis: {
          title: { text: "Phone Number" }
        },
        tooltip: {
          theme: this.chartTheme.mode,
          y: {
            formatter: value => this.money(value)
          }
        },
        grid: { borderColor: this.chartTheme.grid }
      };
    },
    successFailedSeries() {
      const top10 = this.successFailedData.slice(0, 10);
      return [
        {
          name: "Successful",
          data: top10.map(item => item.successfulTransactions || 0)
        },
        {
          name: "Failed",
          data: top10.map(item => item.failedTransactions || 0)
        },
        {
          name: "Pending",
          data: top10.map(item => item.pendingTransactions || 0)
        }
      ];
    },
    successFailedChartOptions() {
      const top10 = this.successFailedData.slice(0, 10);
      return {
        chart: {
          type: "bar",
          height: 350,
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: { show: true }
        },
        theme: { mode: this.chartTheme.mode },
        colors: ["#0f9d63", "#e11d48", "#d9a406"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "60%",
            borderRadius: 4
          }
        },
        dataLabels: { enabled: false },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"]
        },
        xaxis: {
          categories: top10.map(item => {
            const msisdn = item.msisdn || "";
            return msisdn.length > 6 ? "..." + msisdn.slice(-6) : msisdn;
          }),
          title: { text: "Phone Number" }
        },
        yaxis: {
          title: { text: "Number of Transactions" }
        },
        fill: { opacity: 1 },
        tooltip: {
          theme: this.chartTheme.mode,
          y: {
            formatter: value => `${value} transactions`
          }
        },
        legend: { position: "top" },
        grid: { borderColor: this.chartTheme.grid }
      };
    },
    successRateSeries() {
      const top10 = this.successFailedData.slice(0, 10);
      return [
        {
          name: "Success Rate (%)",
          data: top10.map(item => {
            const total = item.totalTransactions || 0;
            const successful = item.successfulTransactions || 0;
            return total > 0 ? ((successful / total) * 100).toFixed(2) : 0;
          })
        }
      ];
    },
    successRateChartOptions() {
      const top10 = this.successFailedData.slice(0, 10);
      return {
        chart: {
          type: "line",
          height: 350,
          background: "transparent",
          foreColor: this.chartTheme.foreColor,
          toolbar: { show: true }
        },
        theme: { mode: this.chartTheme.mode },
        colors: ["#2f6df0"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        markers: {
          size: 5,
          hover: { size: 7 }
        },
        xaxis: {
          categories: top10.map(item => {
            const msisdn = item.msisdn || "";
            return msisdn.length > 6 ? "..." + msisdn.slice(-6) : msisdn;
          }),
          title: { text: "Phone Number" }
        },
        yaxis: {
          title: { text: "Success Rate (%)" },
          min: 0,
          max: 100
        },
        tooltip: {
          theme: this.chartTheme.mode,
          y: {
            formatter: value => `${value}%`
          }
        },
        grid: { borderColor: this.chartTheme.grid }
      };
    }
  },
  filters: {
    currencyFormat(value) {
      return `GHS ${Number(value || 0).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },
    compactNumber(value) {
      const num = parseFloat(value || 0);
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + "M";
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + "K";
      }
      return num.toString();
    },
    compactCurrency(value) {
      const num = parseFloat(value || 0);
      if (num >= 1000000) {
        return `GHS ${(num / 1000000).toFixed(1)}M`;
      } else if (num >= 1000) {
        return `GHS ${(num / 1000).toFixed(1)}K`;
      }
      return `GHS ${num.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  },
  watch: {
    msisdnStatistics: {
      handler(newData) {
        // Calculate success rate for each MSISDN
        newData.forEach(item => {
          const total = item.totalTransactions || 0;
          const successful = item.successfulTransactions || 0;
          item.successRate = total > 0 ? (successful / total) * 100 : 0;
        });
      },
      deep: true
    },
    successFailedOptions: {
      handler(newOptions) {
        const payload = {
          page: newOptions.page,
          size: newOptions.itemsPerPage
        };
        if (this.dates.length === 2) {
          const sortedDates = [...this.dates].sort();
          const [startDate, endDate] = sortedDates;
          payload.startDate = startDate;
          payload.endDate = endDate;
        }
        this.$store.dispatch("reports/getSuccessFailedByMsisdn", payload);
      },
      deep: true
    }
  },
  methods: {
    money(value) {
      return `GHS ${Number(value || 0).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },
    setFilteredDate() {
      this.$refs.menu.save(this.dates);
      if (this.dates.length === 2) {
        const sortedDates = [...this.dates].sort();
        const [startDate, endDate] = sortedDates;
        this.fetchAllData({ startDate, endDate });
      }
    },
    clearDateFilter() {
      this.dates = [];
      this.menu = false;
      this.fetchAllData({});
    },
    refreshData() {
      if (this.dates.length === 2) {
        const sortedDates = [...this.dates].sort();
        const [startDate, endDate] = sortedDates;
        this.fetchAllData({ startDate, endDate });
      } else {
        this.fetchAllData({});
      }
    },
    fetchAllData(payload) {
      this.$store.dispatch("reports/getMsisdnStatistics", payload);
      this.$store.dispatch("reports/getTopMsisdns", {
        ...payload,
        limit: 5
      });
      this.$store.dispatch("reports/getSuccessFailedByMsisdn", {
        ...payload,
        page: this.successFailedOptions.page,
        size: this.successFailedOptions.itemsPerPage
      });
    },
    getSuccessRateColor(rate) {
      if (rate >= 90) return "success";
      if (rate >= 70) return "info";
      if (rate >= 50) return "ember";
      return "error";
    }
  },
  created() {
    this.fetchAllData({});
  }
};
</script>

<style scoped>
.toolbar-btn {
  border-color: var(--border) !important;
  color: var(--muted-foreground) !important;
  border-radius: var(--radius) !important;
  height: 40px !important;
  font-weight: 500;
}
.toolbar-btn--active {
  border-color: var(--signal) !important;
  color: var(--signal) !important;
}
.toolbar__refresh {
  color: var(--muted-foreground);
}

.stat-tile {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-card);
  padding: 18px 20px;
  height: 100%;
}
.stat-tile__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-tile__icon--brand {
  background: var(--signal-soft);
}
.stat-tile__icon--brand .v-icon {
  color: var(--signal);
}
.stat-tile__icon--success {
  background: var(--status-delivered-soft);
}
.stat-tile__icon--success .v-icon {
  color: var(--status-delivered);
}
.stat-tile__icon--failed {
  background: var(--status-failed-soft);
}
.stat-tile__icon--failed .v-icon {
  color: var(--status-failed);
}
.stat-tile__icon--info {
  background: var(--status-sent-soft);
}
.stat-tile__icon--info .v-icon {
  color: var(--status-sent);
}
.stat-tile__label {
  margin: 0 0 3px;
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.stat-tile__value {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 22px;
  font-weight: 600;
  color: var(--foreground);
}

.panel {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  height: 100%;
}
.panel__head {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 15px;
  color: var(--foreground);
}
.panel__body {
  padding: 12px 12px 4px;
}
.panel__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
}
.cell-strong {
  font-weight: 600;
  color: var(--foreground);
}
.cell-amount {
  color: var(--status-sent);
  font-weight: 600;
}
.cell-success {
  color: var(--status-delivered);
  font-weight: 600;
}
.cell-failed {
  color: var(--status-failed);
  font-weight: 600;
}
.cell-ember {
  color: var(--status-ember);
  font-weight: 600;
}
.text-muted {
  color: var(--muted-foreground);
}
</style>
